import { ChevronRight, RemoveRedEyeRounded } from "@mui/icons-material";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddLinkRoundedIcon from "@mui/icons-material/AddLinkRounded";
import InsertLinkRoundedIcon from "@mui/icons-material/InsertLinkRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../../../api/hooks/useCheckUserPermission";
import useColors from "../../../../api/hooks/useColors";
import { serviceNowTicketKeys, ticketKeys } from "../../../../api/queryKeys";
import CustomToolTipWithPermission from "../../../../components/CustomToolTipWithPermission";
import ConfirmationDialog from "../../../../components/dialogs/ConfirmationDialog";
import { Toast } from "../../../../components/Toast";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { PLATFORM_USER_ACTIONS } from "../../../../utils/constants";
import { icons } from "../../environment/icons/iconExport";
import IntegrationImage from "../components/IntegrationImage";

export default function ServiceNowButton({
  fullControl,
  createServiceNowIncident,
  linkServiceNowIncident,
  serviceNowIncidentLink,
  loading,
  sx,
  hideTitle = false,
  disabled = false,
}) {
  const colors = useColors();
  const queryClient = useQueryClient();
  const { trackEvent, closeServiceNowIncident, unlinkTicket } = useAPI();
  const [anchorEl, setAnchorEl] = useState(null);

  const [isRemoveLinkDialogOpen, setIsRemoveLinkDialogOpen] = useState(false);
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.MODIFY_TICKET
  );

  // Open dropdown menu
  const handleServiceNowButtonClick = (event) => {
    const message = "Dropdown menu for 'ServiceNow Incident' button was opened";
    trackEvent(message);
    setAnchorEl(event.currentTarget);
  };

  // Close dropdown menu
  const handleCloseMenu = () => {
    const message = "Dropdown menu for 'ServiceNow Incident' button was closed";
    trackEvent(message);
    setAnchorEl(null);
  };

  // View
  const handleViewButton = () => {
    const message =
      "User clicked on 'View' button under 'ServiceNow Incident' dropdown menu";
    trackEvent(message);

    handleCloseMenu();
    window.open(serviceNowIncidentLink);
  };

  // Create
  const handleCreateServiceNowIncident = () => {
    const message = `User clicked on 'Create ServiceNow Incident' button for control: ${fullControl?.control_id}`;
    trackEvent(message);
    handleCloseMenu();
    createServiceNowIncident();
  };

  // Link
  const handleLinkButton = () => {
    const message =
      "User clicked on 'Link' button under 'ServiceNow Incident' dropdown menu";
    trackEvent(message);
    linkServiceNowIncident();
    handleCloseMenu();
  };

  // Remove link
  const handleRemoveLinkButton = () => {
    const message =
      "User clicked on 'Remove Link' button under 'ServiceNow Incident' dropdown menu";
    trackEvent(message);
    setIsRemoveLinkDialogOpen(true);
  };

  const handleRemoveLinkConfirmButton = async () => {
    const message =
      "User clicked on 'Remove' button for removing ServiceNow incident link";
    trackEvent(message);
    Toast.info(`Removing ServiceNow incident link...`);

    try {
      await unlinkTicket(fullControl.control_id);
      Toast.success(`ServiceNow incident link removed successfully!`);
      queryClient.invalidateQueries({ queryKey: serviceNowTicketKeys.all });
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    } catch (error) {
      Toast.error(
        "Removing ServiceNow incident link failed, please try again later."
      );
    }

    handleCloseDialog("removeLink");
  };

  // Close
  const handleCloseButton = () => {
    const message =
      "User clicked on 'Close' button under 'ServiceNow Incident' dropdown menu";
    trackEvent(message);
    setIsCloseDialogOpen(true);
  };

  // Confirm close
  const handleCloseConfirmButton = () => {
    const message =
      "User clicked on 'Confirm' button for closing ServiceNow incident";
    trackEvent(message);
    Toast.info(`Closing ServiceNow incident...`);

    closeServiceNowIncident(fullControl.control_id)
      .then(() => {
        Toast.success(`ServiceNow incident closed successfully!`);
        queryClient.invalidateQueries({ queryKey: serviceNowTicketKeys.all });
      })
      .catch(() => {
        Toast.error(
          "Closing ServiceNow incident failed, please try again later."
        );
      });

    handleCloseDialog();
  };

  // Close confirmation dialog
  const handleCloseDialog = (action) => {
    if (action === "removeLink") {
      setIsRemoveLinkDialogOpen(false);
    } else {
      setIsCloseDialogOpen(false);
    }

    handleCloseMenu();
  };

  const actions = {
    View: {
      handler: handleViewButton,
      icon: (
        <RemoveRedEyeRounded
          sx={{ fontSize: "16px", color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: true,
    },
    "Create Incident": {
      handler: handleCreateServiceNowIncident,
      icon: (
        <AddCircleOutlineRoundedIcon
          sx={{ fontSize: "16px", color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: false,
      disabled: !hasPermission,
    },
    "Link Existing": {
      handler: handleLinkButton,
      icon: (
        <AddLinkRoundedIcon
          sx={{ fontSize: "16px", color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: false,
      disabled: !hasPermission,
    },
    "Remove Link": {
      handler: handleRemoveLinkButton,
      icon: (
        <LinkOffRoundedIcon
          sx={{ fontSize: "16px", color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: true,
      disabled: !hasPermission,
    },
    "Close Incident": {
      handler: handleCloseButton,
      icon: (
        <SvgToIcon
          IconLogo={icons.DeleteBinIcon}
          width={"16px"}
          style={{ color: colors.icon_action_active }}
        />
      ),
      showWhenThereIsIssue: true,
      disabled: !hasPermission,
    },
  };

  return (
    <>
      <CustomToolTipWithPermission
        title="ServiceNow Incident"
        permission={hasPermission}
        placement="top"
      >
        <IconButton
          sx={{
            height: "32px",
            width: serviceNowIncidentLink ? "auto" : "32px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
            borderRadius: "10px",
            border: "1px solid " + colors.grey_300,
            "&:hover": {
              background: colors.grey_8percent,
              borderColor: colors.gray_main,
            },
            "&.Mui-disabled": {
              opacity: 0.6,
              cursor: "not-allowed",
            },
            ...sx,
          }}
          onClick={handleServiceNowButtonClick}
          disabled={loading || disabled}
        >
          {loading ? (
            <CircularProgress size="1rem" sx={{ color: colors.primary_main }} />
          ) : (
            <>
              <IntegrationImage
                name="servicenow"
                width="16px"
                newLogo={true}
                showToolTip={false}
              />
              {serviceNowIncidentLink && (
                <InsertLinkRoundedIcon
                  sx={{
                    width: "16px",
                    color: colors.action_active,
                  }}
                />
              )}
              {!hideTitle && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  sx={{ paddingLeft: "2px" }}
                >
                  <Typography variant="caption" color={colors.text_primary}>
                    {"ServiceNow Incident"}
                  </Typography>
                  <ChevronRight sx={{ width: "16px" }} />
                </Box>
              )}
            </>
          )}
        </IconButton>
      </CustomToolTipWithPermission>
      <Menu
        id="long-menu"
        MenuListProps={{
          "aria-labelledby": "long-button",
        }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          style: {
            width: "146px",
            borderRadius: "9px",
            boxShadow: colors.dropdown_menu_shadow,
            border: "1px solid" + colors.primary_48percent,
            padding: "4px",
          },
        }}
      >
        {Object.keys(actions)
          .filter(
            (actionName) =>
              actions[actionName].showWhenThereIsIssue ===
              (serviceNowIncidentLink !== null)
          )
          .map((actionName) => (
            <CustomToolTipWithPermission
              key={actionName}
              title={null}
              permission={!actions[actionName].disabled}
            >
              <MenuItem
                onClick={actions[actionName].handler}
                sx={{
                  color: colors.subtitle,
                  padding: 1,
                  gap: 1,
                }}
                disabled={actions[actionName].disabled || disabled}
              >
                {actions[actionName].icon}
                <Typography variant="caption" style={{ textTransform: "none" }}>
                  {actionName}
                </Typography>
              </MenuItem>
            </CustomToolTipWithPermission>
          ))}
      </Menu>

      {/* Remove Link Confirmation Dialog */}
      <ConfirmationDialog
        openDialog={isRemoveLinkDialogOpen}
        handleDialogClose={() => handleCloseDialog("removeLink")}
        handleConfirm={handleRemoveLinkConfirmButton}
        dialogTitle="Remove ServiceNow Incident Link"
        dialogContentFirstLine="Are you sure you want to remove the link to this ServiceNow incident? This will not affect the incident itself in ServiceNow."
        confirmationButtonText="Remove"
        cancelButtonText="Cancel"
      />

      {/* Close Confirmation Dialog */}
      <ConfirmationDialog
        openDialog={isCloseDialogOpen}
        handleDialogClose={() => handleCloseDialog()}
        handleConfirm={handleCloseConfirmButton}
        dialogTitle="Close ServiceNow Incident"
        dialogContentFirstLine="Are you sure you want to close this ServiceNow incident? This will mark the incident as resolved in ServiceNow."
        confirmationButtonText="Close"
        cancelButtonText="Cancel"
      />
    </>
  );
}
